import { API_PATHS, interpolatePathParams, ROUTE_PARAMS } from '@futrli/shared-services';
import { LOADER_AREAS } from '@futrli/ui-components';
import { trackPromise } from 'react-promise-tracker';
import { AllowedAssumptionTypes } from '../../forecast/detail/reports/actions/actions';
import { PreviewImpactsRequestBody } from '../../forecast/prediction-builder/types';
import { AssumptionTypesUnion, ASSUMPTION_TYPES_URL_PARAMS } from '../constants';
import { AiAssumption, QueryParams } from '../types';
import { makeRequest, makeRequestWithResponse } from './shared';

/**
 * Creates a forecast assumption, uses forecast ID and data
 *
 * @param {unknown} data
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {string} forecastId - Forecast id
 * @param {string} assumptionTypeId - type of assumption
 * @param {QueryParams} queryParams
 * @throws error
 * @returns Promise<T>
 */
export const createAssumption = async <T>(
  data: Partial<AllowedAssumptionTypes | AiAssumption>,
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  assumptionTypeId: AssumptionTypesUnion,
  queryParams: QueryParams = {}
): Promise<T> => {
  const path = interpolatePathParams(API_PATHS.FORECAST_ASSUMPTION, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ASSUMPTION_TYPE]: ASSUMPTION_TYPES_URL_PARAMS[assumptionTypeId]
  });

  return trackPromise(
    makeRequestWithResponse<T>({ data, params: queryParams, url: path, method: 'POST' }),
    LOADER_AREAS.predictArea
  );
};

/**
 * Gets a forecast assumption, uses forecast ID, assumption type, assumption ID
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {string} forecastId - Forecast id
 * @param {string} assumptionTypeId - type of assumption
 * @param {string} assumptionId - assumption id
 * @param {QueryParams} queryParams
 * @throws error
 * @returns Promise<T>
 */
export const fetchAssumption = async <T>(
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  assumptionTypeId: AssumptionTypesUnion,
  assumptionId: string,
  queryParams: QueryParams = {}
): Promise<T> => {
  const path = interpolatePathParams(API_PATHS.FORECAST_ASSUMPTION_BY_ID, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ASSUMPTION_TYPE]: ASSUMPTION_TYPES_URL_PARAMS[assumptionTypeId],
    [ROUTE_PARAMS.ASSUMPTION_ID]: assumptionId
  });

  return makeRequestWithResponse<T>({ params: queryParams, url: path, method: 'GET' });
};

/**
 * Updates a forecast assumption, uses forecast ID, assumption type, assumption ID and data
 *
 * @param {unknown} data
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {string} forecastId - Forecast id
 * @param {string} assumptionTypeId - type of assumption
 * @param {string} assumptionId - assumption id
 * @param {QueryParams} queryParams
 * @throws error
 * @returns Promise<void>
 */
export const updateAssumption = async (
  data: Partial<AllowedAssumptionTypes | AiAssumption | { excluded_from_report: boolean }>,
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  assumptionTypeId: AssumptionTypesUnion,
  assumptionId: string,
  queryParams: QueryParams = {}
): Promise<void> => {
  const path = interpolatePathParams(API_PATHS.FORECAST_ASSUMPTION_BY_ID, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ASSUMPTION_TYPE]: ASSUMPTION_TYPES_URL_PARAMS[assumptionTypeId],
    [ROUTE_PARAMS.ASSUMPTION_ID]: assumptionId
  });

  await trackPromise(makeRequest({ data, params: queryParams, url: path, method: 'PATCH' }), LOADER_AREAS.predictArea);
};

/**
 * Updates a forecast assumption data, uses forecast ID, assumption type, assumption ID and data
 *
 * @param {unknown} data
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {string} forecastId - Forecast id
 * @param {string} assumptionTypeId - type of assumption
 * @param {string} assumptionId - assumption id
 * @param {QueryParams} queryParams
 * @throws error
 * @returns Promise<void>
 */
export const updateAssumptionData = async (
  data: unknown,
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  assumptionTypeId: AssumptionTypesUnion,
  assumptionId: string,
  queryParams: QueryParams = {}
): Promise<void> => {
  const path = interpolatePathParams(API_PATHS.FORECAST_BASIC_ASSUMPTION_AMOUNTS, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ASSUMPTION_TYPE]: ASSUMPTION_TYPES_URL_PARAMS[assumptionTypeId],
    [ROUTE_PARAMS.ASSUMPTION_ID]: assumptionId
  });

  await trackPromise(makeRequest({ data, params: queryParams, url: path, method: 'PUT' }), LOADER_AREAS.predictArea);
};

/**
 * Fetches basic assumption amounts
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {string} forecastId - Forecast id
 * @param {string} assumptionTypeId - type of assumption
 * @param {string} assumptionId - assumption id
 * @throws error
 * @returns Promise<T>
 */
export const fetchAssumptionAmounts = async <T>(
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  assumptionTypeId: AssumptionTypesUnion,
  assumptionId: string
): Promise<T> => {
  const path = interpolatePathParams(API_PATHS.FORECAST_BASIC_ASSUMPTION_AMOUNTS, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ASSUMPTION_TYPE]: ASSUMPTION_TYPES_URL_PARAMS[assumptionTypeId],
    [ROUTE_PARAMS.ASSUMPTION_ID]: assumptionId
  });

  return makeRequestWithResponse<T>({ url: path, method: 'GET' });
};

/**
 * Deletes a forecast assumption, uses forecast ID, assumption type, assumption ID
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {string} forecastId - Forecast id
 * @param {string} assumptionTypeId - type of assumption
 * @param {string} assumptionId - assumption id
 * @param {QueryParams} queryParams
 * @throws error
 * @returns Promise<void>
 */
export const deleteAssumption = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  assumptionTypeId: AssumptionTypesUnion,
  assumptionId: string,
  queryParams: QueryParams = {}
): Promise<void> => {
  const path = interpolatePathParams(API_PATHS.FORECAST_ASSUMPTION_BY_ID, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ASSUMPTION_TYPE]: ASSUMPTION_TYPES_URL_PARAMS[assumptionTypeId],
    [ROUTE_PARAMS.ASSUMPTION_ID]: assumptionId
  });

  await makeRequest({ params: queryParams, url: path, method: 'DELETE' });
};

export const previewImpacts = async <T>(
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  data: PreviewImpactsRequestBody
): Promise<T> => {
  const path = interpolatePathParams(API_PATHS.ACCOUNTS_PREVIEW_IMPACTS, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<T>({ data, url: path, method: 'POST' });
};
